/// <amd-module name="Test/TestSummary/Components/Summary" />
import * as React from 'react';
import { ViewContext, ContributionSource } from 'Test/common-util/Common';
import { hasArtifactChanged } from 'Test/common-util/Common.Utils';
import * as TcmPerfScenarios from 'Test/common-util/TCMPerfScenarios';
import * as Telemetry from 'Test/common-util/Telemetry';
import * as TelemetryConstants from 'Test/common-util/TelemetryConstants';
import { TestResultsReportActionsCreator } from 'Test/TestSummary/Actions/TestResultsReportActionsCreator';
import { CompletedSummaryView } from 'Test/TestSummary/Components/CompletedSummaryView';
import { InProgressSummaryView } from 'Test/TestSummary/Components/InProgressSummaryView';
import { ISummaryProps, ISummaryViewState } from 'Test/TestSummary/Report.types';
import * as Resources from 'Test/TestSummary/Resources';
import { SummaryViewStore } from 'Test/TestSummary/Store/SummaryViewStore';
import { IVssComponentContext, VssComponent } from 'VSS/Platform/Layout';
import { IVssPerformanceService } from 'VSS/Platform/Performance';
import { Accordion, IAccordionSection,IAccordionProps} from "VSSUI/Accordion";
import { ObservableValue } from 'VSS/Core/Observable';

export class Summary extends VssComponent<ISummaryProps, ISummaryViewState, IVssComponentContext>{
    private _performanceService: IVssPerformanceService;
    constructor(props: ISummaryProps, context: IVssComponentContext) {
        super(props, context);
        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._summaryActionsCreator = new TestResultsReportActionsCreator(this.props.actionsHub, context, this.props.artifactData);
        this._summaryViewStore = new SummaryViewStore("TestTabInBuildComponent",
            context,
            this.props.actionsHub,
            this.props.artifactData);

        this.state = this._summaryViewStore.getState();
    }

    public componentDidUpdate(prevProps: ISummaryProps) {
        if (hasArtifactChanged(this.props.artifactData, prevProps.artifactData)) {
            // re initializing the summary data, if the status has been changed.
            this._summaryActionsCreator.initialize(this.props.artifactData);
        }
    }

    public componentDidMount() {
        this._summaryViewStore.addChangedListener(this._handleStoreChange);
        this._summaryActionsCreator.initialize(this.props.artifactData);

        // End this after setting state from the store.
        let contributionSource: string | undefined = this.props.artifactData.contributionSource;
        if (contributionSource === ContributionSource.Build || contributionSource === ContributionSource.Pipeline) {
            this._performanceService.addSplitTiming(TcmPerfScenarios.TestSummaryLoaded, contributionSource);
        }
    }

    public componentWillUnmount() {
        this._summaryViewStore.removeChangedListener(this._handleStoreChange);
        this._summaryViewStore.dispose();
    }

    public render(): JSX.Element | null {
        // Returning null, so that parent will handle the loading scenario.
        if (!this.state.shouldShowSummary) {
            return null;
        }

        const summaryComponent: JSX.Element = this.state.isInProgressView ?
            <InProgressSummaryView store={this._summaryViewStore} artifactData={this.props.artifactData} telemetryPropertiesForInProductLink={this._getTelemetryPropertiesForInProductLink()}></InProgressSummaryView> :
            <CompletedSummaryView store={this._summaryViewStore} artifactData={this.props.artifactData} telemetryPropertiesForInProductLink={this._getTelemetryPropertiesForInProductLink()}></CompletedSummaryView>
        const items: IAccordionSection[] = [
            {
                isCollapsed: new ObservableValue(this.state.summaryAccordianState),
                key: this._testSummaryString,
                title: Resources.TestSummaryTitle,
                onRenderContent: () => { return summaryComponent; }
            }
        ];

        // added space before className to get two class names for the same component
        let summaryClassName: string = this.props.artifactData.contributionSource === ContributionSource.Pipeline ? "test-results-summary-view pipeline-summary" : "test-results-summary-view";
        return <div className={summaryClassName}>
            <div className="test-summary-view depth-8">
                <Accordion
                    sections={items}
                    className={"summary-accordion"}
                    onSectionClicked = {this.onSectionClicked}
                />
            </div>
        </div>
    }
   
    private onSectionClicked = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, section: IAccordionSection): void => {
        const collapsed = section.isCollapsed as ObservableValue<boolean>;
        collapsed.value = !collapsed.value;
        let tmpState = this._summaryViewStore.getState();
        tmpState.summaryAccordianState = collapsed.value;
        this.setState(tmpState);
        this._summaryExpandedOrCollapsedTelemetry(this._testSummaryString,collapsed.value);

    };

    private _summaryExpandedOrCollapsedTelemetry(key: string, isExpanded: boolean) {
        if (key == this._testSummaryString) {
            Telemetry.publishEvent(this.context.pageContext, this.props.artifactData, TelemetryConstants.featureTestTab_SummaryExpandCollapseClicked, { "State": isExpanded });
        }
    }

    private _handleStoreChange = () => {
        const changedState = this._summaryViewStore.getState();
        if (changedState.refreshSummary) {
            // In progress summary refresh.
            this._summaryActionsCreator.refreshTestResulstsSummary(this.props.artifactData);
            return;
        }

        this.setState(changedState);
    }

    private _getTelemetryPropertiesForInProductLink(): { [propertyName: string]: any } {
        let telemetryProperties: { [propertyName: string]: any } = {
            [TelemetryConstants.workflow]: ViewContext[this.props.artifactData.viewContext].toString(),
            [TelemetryConstants.id]: this.props.artifactData.data.mainData.id.toString()
        };
        return telemetryProperties;
    }

    private _testSummaryString: string = "TestSummary";
    private _summaryViewStore: SummaryViewStore;
    private _summaryActionsCreator: TestResultsReportActionsCreator;
}